import React, { useState, useEffect, useCallback } from 'react';
import adminApi from '../services/adminApi';

function PushNotificationsPage() {
  const [stats, setStats] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/');
  const [target, setTarget] = useState('all');
  const [userId, setUserId] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const data = await adminApi.getPushStats();
      setStats(data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;

    setSending(true);
    setResult(null);

    try {
      const data = await adminApi.sendPushNotification({
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || '/',
        target: target === 'user' ? userId.trim() : 'all',
      });
      setResult({ success: true, ...data });
      fetchStats();
    } catch (err) {
      setResult({ success: false, error: err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Push Notifications</h1>
        {stats && (
          <span style={{
            background: '#e8f5e9',
            color: '#2e7d32',
            padding: '6px 14px',
            borderRadius: 20,
            fontSize: 14,
            fontWeight: 600,
          }}>
            {stats.subscriberCount} active subscriber{stats.subscriberCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <form onSubmit={handleSend} style={{ maxWidth: 560 }}>
        {/* Target */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Target</label>
          <div style={{ display: 'flex', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="radio"
                name="target"
                value="all"
                checked={target === 'all'}
                onChange={() => setTarget('all')}
              />
              All subscribers
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="radio"
                name="target"
                value="user"
                checked={target === 'user'}
                onChange={() => setTarget('user')}
              />
              Specific user
            </label>
          </div>
          {target === 'user' && (
            <input
              type="text"
              placeholder="User ID (MongoDB ObjectId)"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              style={{ ...inputStyle, marginTop: 8 }}
            />
          )}
        </div>

        {/* Title */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Your meal plan expires soon!"
            maxLength={100}
            required
            style={inputStyle}
          />
        </div>

        {/* Body */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Message *</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="e.g. Open the app to refresh your meal plan before it expires."
            maxLength={300}
            required
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {/* URL */}
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Link URL (optional)</label>
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/"
            style={inputStyle}
          />
          <p style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            Where to navigate when the notification is tapped (e.g. /mymeals, /wizard)
          </p>
        </div>

        <button
          type="submit"
          disabled={sending || !title.trim() || !body.trim()}
          style={{
            background: sending ? '#ccc' : '#FFCB00',
            color: '#0D0C07',
            border: 'none',
            borderRadius: 8,
            padding: '10px 28px',
            fontSize: 15,
            fontWeight: 600,
            cursor: sending ? 'not-allowed' : 'pointer',
          }}
        >
          {sending ? 'Sending…' : 'Send Notification'}
        </button>
      </form>

      {/* Result feedback */}
      {result && (
        <div
          style={{
            marginTop: 20,
            padding: 16,
            borderRadius: 8,
            background: result.success ? '#e8f5e9' : '#fbe9e7',
            maxWidth: 560,
          }}
        >
          {result.success ? (
            <p style={{ margin: 0, color: '#2e7d32' }}>
              ✅ Sent to {result.sent}/{result.total} subscriber{result.total !== 1 ? 's' : ''}.
              {result.deactivated > 0 && ` (${result.deactivated} expired subscriptions removed)`}
            </p>
          ) : (
            <p style={{ margin: 0, color: '#c62828' }}>❌ {result.error}</p>
          )}
        </div>
      )}
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: 14,
  fontWeight: 600,
  marginBottom: 6,
  color: '#333',
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #ddd',
  fontSize: 14,
  boxSizing: 'border-box',
};

export default PushNotificationsPage;
